import React from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { useStochasticDashboard } from "@/modules/stochastic/features/Reports/SCDashboard/hooks/useStochasticDashboard";
import { useDashboardChartConfig } from "@/modules/stochastic/features/Reports/SCDashboard/hooks/useDashboardChartConfig";
import {
  orderChartsForTab,
  renderChart,
  renderChartSkeleton,
} from "@/modules/stochastic/features/Reports/SCDashboard/config/chartRegistry";
import { StochasticDashboardFilters } from "@/modules/stochastic/features/Reports/SCDashboard/components/DashboardFilters/StochasticDashboardFilters";
import { ChartIndex } from "@/modules/stochastic/features/Reports/SCDashboard/components/ChartIndex/ChartIndex";
import { LastSyncStatus } from "@/modules/stochastic/features/shared/components/LastSyncStatus/LastSyncStatus";

export default function DashboardPage() {
  const {
    dashboardData,
    dashboardFiltersForm,
    loading,
    refreshing,
    activeTab,
    setActiveTab,
    applyFilters,
    areFiltersDirty,
    handleRefresh,
    error,
  } = useStochasticDashboard();

  // Stage-curated chart list (#9292): which charts this company sees, and
  // in what order. null = ungated (CertainPath, no stage, or fetch issue).
  const { chartKeys, configLoading } = useDashboardChartConfig();

  const visibleChartIds = orderChartsForTab(activeTab, chartKeys);

  const refreshButton = (
    <div className="flex items-center gap-3">
      <LastSyncStatus lastSync={dashboardData.lastSync} />
      <button
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
        disabled={refreshing}
        onClick={handleRefresh}
      >
        <ArrowPathIcon
          className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
        />
        Refresh
      </button>
    </div>
  );

  const showSkeletons = loading || configLoading;

  return (
    <MainPageWrapper
      actions={refreshButton}
      loading={loading}
      title="Sales and Customer Dashboard"
    >
      <div className="bg-gray-50 dark:bg-slate-950 min-h-screen transition-colors duration-200">
        {error && !loading && (
          <div className="mb-4 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm text-gray-900 dark:text-white">
            Dashboard data couldn&apos;t be loaded, so charts may be empty. Use
            Refresh to try again.
          </div>
        )}
        <StochasticDashboardFilters
          activeTab={activeTab}
          areFiltersDirty={areFiltersDirty}
          chartFiltersForm={dashboardFiltersForm}
          onApplyFilters={applyFilters}
          setActiveTab={setActiveTab}
        />
        <div className="flex gap-6 pt-2">
          <ChartIndex activeTab={activeTab} visibleChartIds={visibleChartIds} />
          <div className="flex-1 min-w-0 overflow-hidden space-y-6 [&_.rounded-lg.border]:bg-white dark:[&_.rounded-lg.border]:bg-slate-800">
            {showSkeletons
              ? visibleChartIds.map((id) => (
                  <React.Fragment key={id}>
                    {renderChartSkeleton(id)}
                  </React.Fragment>
                ))
              : visibleChartIds.map((id) => (
                  <div id={id} key={id}>
                    {renderChart(id, dashboardData)}
                  </div>
                ))}
          </div>
        </div>
      </div>
    </MainPageWrapper>
  );
}
